import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { scorecardKeys } from "./queryKeys";
import {
	deleteScorecardCategory,
	deleteScorecardField,
	fetchScorecardCategories,
	fetchScorecardFields,
	reorderScorecardRows,
	upsertScorecardCategory,
	upsertScorecardField,
} from "@/integrations/supabase/scorecardApi";
import type {
	ScorecardCategory,
	ScorecardCategoryInput,
	ScorecardField,
	ScorecardFieldInput,
} from "@/types/scorecard";

const CONFIG_STALE_TIME = 5 * 60 * 1000;

/** Org-wide scorecard categories, ordered by display_order. */
export function useScorecardCategories(organizationId: string | undefined) {
	const query = useQuery({
		queryKey: scorecardKeys.categories(organizationId || "none"),
		queryFn: () => fetchScorecardCategories(organizationId!),
		enabled: !!organizationId,
		staleTime: CONFIG_STALE_TIME,
	});
	return {
		...query,
		categories: query.data ?? [],
	};
}

/**
 * Org-wide scorecard field definitions, ordered by display_order.
 * `hasFields` also gates the candidate Scorecard tab's visibility, so the
 * tab bar and the tab content share one cached fetch.
 */
export function useScorecardFields(organizationId: string | undefined) {
	const query = useQuery({
		queryKey: scorecardKeys.fields(organizationId || "none"),
		queryFn: () => fetchScorecardFields(organizationId!),
		enabled: !!organizationId,
		staleTime: CONFIG_STALE_TIME,
	});
	return {
		...query,
		fields: query.data ?? [],
		hasFields: (query.data?.length ?? 0) > 0,
	};
}

/** Create or update a category; errors propagate to the caller. */
export function useSaveScorecardCategory(organizationId: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (category: ScorecardCategoryInput) =>
			upsertScorecardCategory(organizationId!, category),
		onSuccess: () => invalidateConfig(queryClient, organizationId),
	});
}

/** Delete a category; its fields keep existing as uncategorized (FK is nullable). */
export function useDeleteScorecardCategory(organizationId: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (categoryId: string) => deleteScorecardCategory(categoryId),
		onSuccess: () => invalidateConfig(queryClient, organizationId),
	});
}

/** Create or update a field definition; errors propagate to the caller. */
export function useSaveScorecardField(organizationId: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (field: ScorecardFieldInput) => upsertScorecardField(organizationId!, field),
		onSuccess: () => invalidateConfig(queryClient, organizationId),
	});
}

export function useDeleteScorecardField(organizationId: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (fieldId: string) => deleteScorecardField(fieldId),
		onSuccess: () => invalidateConfig(queryClient, organizationId),
	});
}

interface ReorderContext {
	previous: unknown;
}

/**
 * Builds an optimistic reorder mutation for fields or categories: the cached
 * list is re-sorted immediately, rolled back on error, and re-fetched on
 * settle so display_order values converge with the server.
 */
function useReorderMutation<T extends { id: string }>(
	table: "scorecard_fields" | "scorecard_categories",
	organizationId: string | undefined,
) {
	const queryClient = useQueryClient();
	const queryKey =
		table === "scorecard_fields"
			? scorecardKeys.fields(organizationId || "none")
			: scorecardKeys.categories(organizationId || "none");

	return useMutation<void, Error, { id: string; display_order: number }[], ReorderContext>({
		mutationFn: (updates) => reorderScorecardRows(table, updates),
		onMutate: async (updates) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData(queryKey);
			const orderById = new Map(updates.map((update) => [update.id, update.display_order]));
			queryClient.setQueryData<T[]>(queryKey, (rows) =>
				[...(rows ?? [])]
					.map((row) =>
						orderById.has(row.id)
							? { ...row, display_order: orderById.get(row.id) }
							: row,
					)
					.sort(
						(a, b) =>
							((a as { display_order?: number }).display_order ?? 0) -
							((b as { display_order?: number }).display_order ?? 0),
					),
			);
			return { previous };
		},
		onError: (_error, _updates, context) => {
			if (context) queryClient.setQueryData(queryKey, context.previous);
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
}

export function useReorderScorecardFields(organizationId: string | undefined) {
	return useReorderMutation<ScorecardField>("scorecard_fields", organizationId);
}

export function useReorderScorecardCategories(organizationId: string | undefined) {
	return useReorderMutation<ScorecardCategory>("scorecard_categories", organizationId);
}

function invalidateConfig(
	queryClient: ReturnType<typeof useQueryClient>,
	organizationId: string | undefined,
) {
	// Fields embed their category, so category changes invalidate both lists.
	queryClient.invalidateQueries({ queryKey: scorecardKeys.fields(organizationId || "none") });
	queryClient.invalidateQueries({ queryKey: scorecardKeys.categories(organizationId || "none") });
}
